import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Lock, Eye, Trash2, Cookie, Users, Bell, FileText } from 'lucide-react';

const PrivacyPolicy = () => {
  const effectiveDate = "August 2, 2025";

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center space-x-2">
              <img 
                src="/big-clipboard-text-logo.jpg" 
                alt="Big Clipboard" 
                className="h-12 w-auto"
              />
            </Link>
            <Link 
              to="/" 
              className="flex items-center space-x-2 text-gray-600 hover:text-primary-600 transition-colors duration-200"
            >
              <ArrowLeft className="h-5 w-5" />
              <span>Back to Home</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Header Section */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 rounded-full mb-6">
              <Shield className="h-8 w-8 text-primary-600" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Big Clipboard Privacy Policy</h1>
            <p className="text-lg text-gray-600">Effective Date: {effectiveDate}</p>
          </div>

          {/* Policy Content */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 md:p-12">
            <div className="prose prose-lg max-w-none">
              <p className="text-gray-700 mb-8">
                Big Clipboard ("we," "our," or "us") respects your privacy and is committed to protecting your personal data. 
                This Privacy Policy explains how we collect, use, and safeguard your information when you use our website, 
                browser extension, and related services (collectively, the "Service").
              </p>

              {/* Section 1 */}
              <div className="mb-8">
                <div className="flex items-center mb-4">
                  <div className="flex items-center justify-center w-8 h-8 bg-blue-100 rounded-full mr-3">
                    <span className="text-blue-600 font-semibold text-sm">1</span>
                  </div>
                  <h2 className="text-2xl font-semibold text-gray-900">Information We Collect</h2>
                </div>
                <p className="text-gray-700 mb-4">We collect the following types of information:</p>
                <ul className="space-y-3 mb-4">
                  <li className="flex items-start">
                    <div className="flex items-center justify-center w-6 h-6 bg-green-100 rounded-full mr-3 mt-0.5">
                      <Users className="h-3 w-3 text-green-600" />
                    </div>
                    <span className="text-gray-700">
                      <strong>Account Information</strong> – When you create an account, we collect your name, email address, and password.
                    </span>
                  </li>
                  <li className="flex items-start">
                    <div className="flex items-center justify-center w-6 h-6 bg-purple-100 rounded-full mr-3 mt-0.5">
                      <FileText className="h-3 w-3 text-purple-600" />
                    </div>
                    <span className="text-gray-700">
                      <strong>Clipboard Data</strong> – We store the items you copy (text, images, links, etc.) so they can be accessed across your devices.
                    </span>
                  </li>
                  <li className="flex items-start">
                    <div className="flex items-center justify-center w-6 h-6 bg-orange-100 rounded-full mr-3 mt-0.5">
                      <Eye className="h-3 w-3 text-orange-600" />
                    </div>
                    <span className="text-gray-700">
                      <strong>Usage Information</strong> – We may collect information about how you use our Service to improve functionality and performance.
                    </span>
                  </li>
                </ul>
                <p className="text-gray-700 font-medium">
                  We do not sell, rent, or trade your personal data to third parties.
                </p>
              </div>

              {/* Section 2 */}
              <div className="mb-8">
                <div className="flex items-center mb-4">
                  <div className="flex items-center justify-center w-8 h-8 bg-blue-100 rounded-full mr-3">
                    <span className="text-blue-600 font-semibold text-sm">2</span>
                  </div>
                  <h2 className="text-2xl font-semibold text-gray-900">How We Use Your Information</h2>
                </div>
                <p className="text-gray-700 mb-4">We use the information collected to:</p>
                <ul className="space-y-2 mb-4">
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-primary-600 rounded-full mr-3 mt-2"></div>
                    <span className="text-gray-700">Provide and improve our Service</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-primary-600 rounded-full mr-3 mt-2"></div>
                    <span className="text-gray-700">Sync clipboard data between your devices</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-primary-600 rounded-full mr-3 mt-2"></div>
                    <span className="text-gray-700">Securely store your clipboard history</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-primary-600 rounded-full mr-3 mt-2"></div>
                    <span className="text-gray-700">Communicate with you about updates or support requests</span>
                  </li>
                </ul>
              </div>

              {/* Section 3 */}
              <div className="mb-8">
                <div className="flex items-center mb-4">
                  <div className="flex items-center justify-center w-8 h-8 bg-blue-100 rounded-full mr-3">
                    <span className="text-blue-600 font-semibold text-sm">3</span>
                  </div>
                  <h2 className="text-2xl font-semibold text-gray-900">Data Sharing</h2>
                </div>
                <p className="text-gray-700 mb-4">
                  We do not share your personal information with third parties except:
                </p>
                <ul className="space-y-2 mb-4">
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-primary-600 rounded-full mr-3 mt-2"></div>
                    <span className="text-gray-700">When required by law</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-primary-600 rounded-full mr-3 mt-2"></div>
                    <span className="text-gray-700">To protect our legal rights or prevent misuse of our Service</span>
                  </li>
                </ul>
                <p className="text-gray-700 font-medium">
                  All data is encrypted and stored securely.
                </p>
              </div>

              {/* Section 4 */}
              <div className="mb-8">
                <div className="flex items-center mb-4">
                  <div className="flex items-center justify-center w-8 h-8 bg-blue-100 rounded-full mr-3">
                    <span className="text-blue-600 font-semibold text-sm">4</span>
                  </div>
                  <h2 className="text-2xl font-semibold text-gray-900">Data Retention</h2>
                </div>
                <p className="text-gray-700">
                  We retain your data only as long as necessary to provide our Service. 
                  You may request deletion of your data at any time (see Section 6 below).
                </p>
              </div>

              {/* Section 5 */}
              <div className="mb-8">
                <div className="flex items-center mb-4">
                  <div className="flex items-center justify-center w-8 h-8 bg-blue-100 rounded-full mr-3">
                    <span className="text-blue-600 font-semibold text-sm">5</span>
                  </div>
                  <h2 className="text-2xl font-semibold text-gray-900">Security</h2>
                </div>
                <div className="flex items-start mb-4">
                  <div className="flex items-center justify-center w-6 h-6 bg-green-100 rounded-full mr-3 mt-0.5">
                    <Lock className="h-3 w-3 text-green-600" />
                  </div>
                  <p className="text-gray-700">
                    We use industry-standard security measures to protect your data. However, no method of transmission 
                    or storage is 100% secure, and we cannot guarantee absolute security.
                  </p>
                </div>
              </div>

              {/* Section 6 */}
              <div className="mb-8">
                <div className="flex items-center mb-4">
                  <div className="flex items-center justify-center w-8 h-8 bg-blue-100 rounded-full mr-3">
                    <span className="text-blue-600 font-semibold text-sm">6</span>
                  </div>
                  <h2 className="text-2xl font-semibold text-gray-900">Your Data Deletion Rights</h2>
                </div>
                <div className="flex items-start mb-4">
                  <div className="flex items-center justify-center w-6 h-6 bg-red-100 rounded-full mr-3 mt-0.5">
                    <Trash2 className="h-3 w-3 text-red-600" />
                  </div>
                  <div>
                    <p className="text-gray-700 mb-2">
                      You have the right to request the deletion of all your data at any time.
                    </p>
                    <p className="text-gray-700 mb-2">
                      To do so, simply submit a request through the contact form on our website.
                    </p>
                    <p className="text-gray-700">
                      Once your request is received and verified, we will permanently delete all associated data from our systems.
                    </p>
                  </div>
                </div>
              </div>

              {/* Section 7 */}
              <div className="mb-8">
                <div className="flex items-center mb-4">
                  <div className="flex items-center justify-center w-8 h-8 bg-blue-100 rounded-full mr-3">
                    <span className="text-blue-600 font-semibold text-sm">7</span>
                  </div>
                  <h2 className="text-2xl font-semibold text-gray-900">Cookies & Tracking</h2>
                </div>
                <div className="flex items-start mb-4">
                  <div className="flex items-center justify-center w-6 h-6 bg-yellow-100 rounded-full mr-3 mt-0.5">
                    <Cookie className="h-3 w-3 text-yellow-600" />
                  </div>
                  <p className="text-gray-700">
                    We may use cookies to enhance user experience, analyze usage, and improve our Service. 
                    You can disable cookies in your browser settings, but some features may not function properly.
                  </p>
                </div>
              </div>

              {/* Section 8 */}
              <div className="mb-8">
                <div className="flex items-center mb-4">
                  <div className="flex items-center justify-center w-8 h-8 bg-blue-100 rounded-full mr-3">
                    <span className="text-blue-600 font-semibold text-sm">8</span>
                  </div>
                  <h2 className="text-2xl font-semibold text-gray-900">Third-Party Services</h2>
                </div>
                <p className="text-gray-700">
                  Big Clipboard may use third-party services (such as analytics or hosting providers) that have access 
                  to limited data to perform their functions. These providers are obligated to protect your information.
                </p>
              </div>

              {/* Section 9 */}
              <div className="mb-8">
                <div className="flex items-center mb-4">
                  <div className="flex items-center justify-center w-8 h-8 bg-blue-100 rounded-full mr-3">
                    <span className="text-blue-600 font-semibold text-sm">9</span>
                  </div>
                  <h2 className="text-2xl font-semibold text-gray-900">Children's Privacy</h2>
                </div>
                <p className="text-gray-700">
                  Our Service is not directed to individuals under the age of 13. We do not knowingly collect personal information from children.
                </p>
              </div>

              {/* Section 10 */}
              <div className="mb-8">
                <div className="flex items-center mb-4">
                  <div className="flex items-center justify-center w-8 h-8 bg-blue-100 rounded-full mr-3">
                    <span className="text-blue-600 font-semibold text-sm">10</span>
                  </div>
                  <h2 className="text-2xl font-semibold text-gray-900">Changes to This Policy</h2>
                </div>
                <div className="flex items-start mb-4">
                  <div className="flex items-center justify-center w-6 h-6 bg-blue-100 rounded-full mr-3 mt-0.5">
                    <Bell className="h-3 w-3 text-blue-600" />
                  </div>
                  <p className="text-gray-700">
                    We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated effective date.
                  </p>
                </div>
              </div>

              {/* Section 11 */}
              <div className="mb-8">
                <div className="flex items-center mb-4">
                  <div className="flex items-center justify-center w-8 h-8 bg-blue-100 rounded-full mr-3">
                    <span className="text-blue-600 font-semibold text-sm">11</span>
                  </div>
                  <h2 className="text-2xl font-semibold text-gray-900">Contact Us</h2>
                </div>
                <p className="text-gray-700">
                  If you have questions about this Privacy Policy or wish to request deletion of your data, 
                  please contact us via the contact form on our website.
                </p>
              </div>

              {/* Conclusion */}
              <div className="mt-12 p-6 bg-gray-50 rounded-lg border border-gray-200">
                <p className="text-gray-700 font-medium text-center">
                  By using Big Clipboard, you agree to the terms of this Privacy Policy.
                </p>
              </div>
            </div>
          </div>

          {/* Back to Home Button */}
          <div className="text-center mt-8">
            <Link 
              to="/" 
              className="inline-flex items-center space-x-2 bg-primary-600 hover:bg-primary-700 text-white font-medium py-3 px-6 rounded-lg transition-colors duration-200"
            >
              <ArrowLeft className="h-5 w-5" />
              <span>Back to Home</span>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy; 